/**
 * turn envelopes -> the turn/message structure LearningTranscript renders.
 *
 * WHY THIS EXISTS
 *   The in-session overlay hands LearningTranscript the dashboard's live
 *   `messages` array. /learning/{id} has no live session: all it has is the
 *   `turns` array the trail endpoint now returns (built by
 *   server/app/learning/transcript.py::session_turns). One turn envelope is
 *
 *     {n, ts, user, assistant, mode, stopped, sources[], question|null,
 *      cache_hit?}
 *
 *   — `ts` is epoch SECONDS, `question` is `{text, options[], pending}` or
 *   null, `cache_hit` is present only when true, and `panels`/`info`/`blocks`
 *   are deliberately dropped server-side. This module turns that into the
 *   dashboard message shapes MessageRow already knows how to draw, keeping
 *   the review pane and the overlay pixel-identical.
 *
 * TURN NUMBERING IS THE CONTRACT
 *   A step's `turn_no` is the server's commit turn number, which is exactly
 *   the envelope's `n` (sockets_tse.py stamps both from `_last_commit_turn_no`).
 *   Clicking a step scrolls the transcript to that turn, so this emits
 *   PRE-GROUPED turns carrying `n` verbatim rather than letting
 *   LearningTranscript re-derive numbers by counting user bubbles — a session
 *   with a gap (a turn with no user text, or one dropped by the MAX_TURNS cap)
 *   would otherwise renumber everything after it and break the linkage.
 *
 * NEVER THROWS. Malformed input yields fewer messages, never an exception —
 * a trail must still review when its transcript is unreadable.
 */

/** Epoch seconds (what the server stores) -> ms (what Date wants). */
const toMillis = (raw) => {
  const num = typeof raw === "number" ? raw : Number(raw);
  if (Number.isFinite(num) && num > 0) return num > 1e12 ? num : num * 1000;
  return undefined;
};

const text = (v) => (typeof v === "string" ? v : "");

/** The MCQ snapshot a turn displayed, or null. */
const questionOf = (turn) => {
  const q = turn && turn.question;
  if (!q || typeof q !== "object") return null;
  const qText = text(q.text);
  const options = Array.isArray(q.options) ? q.options.filter((o) => typeof o === "string") : [];
  if (!qText && !options.length) return null;
  return { text: qText, options };
};

/**
 * Convert `turns` from the trail endpoint into
 * `[{turnNo, messages: [...]}]`, oldest first.
 *
 * Returns [] for anything that is not a non-empty array — the caller uses that
 * to fall back to the old step-derived pane.
 */
export function turnsToTranscript(turns) {
  if (!Array.isArray(turns) || !turns.length) return [];

  // Server already sorts, but a stable local sort costs nothing and keeps the
  // step<->turn scroll honest if it ever stops.
  const ordered = turns
    .filter((t) => t && typeof t === "object")
    .map((t, i) => {
      const n = Number(t.n);
      return { env: t, turnNo: Number.isFinite(n) ? n : i + 1 };
    })
    .sort((a, b) => a.turnNo - b.turnNo);

  const out = [];
  // The question the PREVIOUS turn ended on. Live (and in the dashboard's own
  // replay) a reply matching one of its options is frozen as an
  // answered_question card INSTEAD of a plain user bubble, so this makes the
  // same call to land on the same transcript.
  let pendingQuestion = null;
  let pendingQuestionTurn = null;

  for (const { env, turnNo } of ordered) {
    const ts = toMillis(env.ts);
    const messages = [];
    const userText = text(env.user).trim();

    if (pendingQuestion) {
      const lower = userText.toLowerCase();
      const matched = userText
        ? pendingQuestion.options.find((o) => o.trim().toLowerCase() === lower)
        : undefined;
      if (matched !== undefined) {
        // The answer card carries the user's choice, so it IS this turn's
        // opening message and no separate bubble is emitted.
        messages.push({
          id: `lt-${turnNo}-aq`,
          type: "answered_question",
          sender: "user",
          text: matched,
          questionSnapshot: {
            question: pendingQuestion.text,
            options: pendingQuestion.options,
          },
          selectedAnswer: matched,
          askedAt: pendingQuestion.askedAt,
          timestamp: ts,
        });
      } else {
        // Displayed but not answered by an option — the card belongs at the
        // end of the turn that showed it, with no selection.
        pendingQuestionTurn.messages.push({
          id: `lt-${pendingQuestionTurn.turnNo}-q`,
          type: "answered_question",
          sender: "copilot",
          questionSnapshot: {
            question: pendingQuestion.text,
            options: pendingQuestion.options,
          },
          askedAt: pendingQuestion.askedAt,
          timestamp: pendingQuestion.askedAt,
        });
      }
      pendingQuestion = null;
      pendingQuestionTurn = null;
    }

    if (userText && !messages.length) {
      // sender "user", not "agent": the d5 meta line prefixes an "agent"
      // bubble with "You", and the reader here is an admin who was never in
      // this session.
      messages.push({
        id: `lt-${turnNo}-u`,
        sender: "user",
        text: userText,
        timestamp: ts,
      });
    }

    const sources = Array.isArray(env.sources) ? env.sources.filter(Boolean) : [];
    const assistant = text(env.assistant);
    if (assistant || sources.length) {
      messages.push({
        id: `lt-${turnNo}-a`,
        sender: "copilot",
        text: assistant,
        sources: sources.length ? sources : undefined,
        cacheHit: env.cache_hit === true,
        isDone: true,
        timestamp: ts,
      });
    }

    if (env.stopped) {
      // The engineer cut this answer off part-way. Hiding that would
      // misrepresent a half-written answer as the engine's final word.
      messages.push({
        id: `lt-${turnNo}-stopped`,
        type: "status",
        text: "Answer stopped by the engineer",
        timestamp: ts,
      });
    }

    const entry = { turnNo, messages };
    out.push(entry);

    const q = questionOf(env);
    if (q) {
      pendingQuestion = { ...q, askedAt: ts };
      pendingQuestionTurn = entry;
    }
  }

  // A question on the final turn was never answered — still shown, unselected.
  if (pendingQuestion && pendingQuestionTurn) {
    pendingQuestionTurn.messages.push({
      id: `lt-${pendingQuestionTurn.turnNo}-q`,
      type: "answered_question",
      sender: "copilot",
      questionSnapshot: {
        question: pendingQuestion.text,
        options: pendingQuestion.options,
      },
      askedAt: pendingQuestion.askedAt,
      timestamp: pendingQuestion.askedAt,
    });
  }

  // A turn that produced nothing renderable (no user text, no answer, no
  // sources, no question) would draw a bare "Turn N" header over nothing.
  return out.filter((t) => t.messages.length > 0);
}

export default turnsToTranscript;
